import type { Vehicle } from '@otw/models'
import { axiosClient } from 'core/api/axiosClient'
import {
  fetchActiveInventoryVehicles,
  fetchPurchasedForInventoryVehicles,
  fetchSoldVehiclesInRange,
  fetchVehiclesReadyForTransport,
  fetchWarrantyVehicles,
} from 'hooks/inventory/fetchMasterInventory'

export type DashboardVehiclesApiBundle = {
  startMs: number
  endMs: number
  sold: Vehicle[]
  active: Vehicle[]
  warranty: Vehicle[]
  purchasedForInventory: Vehicle[]
  readyForTransport: Vehicle[]
}

type BundleCache = {
  key: string
  promise: Promise<DashboardVehiclesApiBundle>
}

let bundleCache: BundleCache | null = null
let registeredRangeMs: { startMs: number; endMs: number } | null = null

const bundleKey = (startMs: number, endMs: number) => `${startMs}:${endMs}`

/** Set from AdminDashboard date selector so all slices share one bundle key. */
export const registerDashboardVehicleRange = (startMs: number, endMs: number): void => {
  registeredRangeMs = { startMs, endMs }
}

export const getDashboardVehicleRangeMs = (): { startMs: number; endMs: number } => {
  if (registeredRangeMs) return registeredRangeMs
  const end = new Date()
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  return {
    startMs: start.getTime(),
    endMs: new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime(),
  }
}

const loadBundleFromApi = async (startMs: number, endMs: number): Promise<DashboardVehiclesApiBundle> => {
  const data = (await axiosClient.get('/portal/admin/dashboard/vehicles', {
    params: { startMs, endMs },
  })) as DashboardVehiclesApiBundle & { error?: string }

  if (!Array.isArray(data?.sold) || !Array.isArray(data?.active)) {
    throw new Error('Invalid dashboard vehicles API payload')
  }

  return {
    startMs,
    endMs,
    sold: data.sold,
    active: data.active,
    warranty: data.warranty ?? [],
    purchasedForInventory: data.purchasedForInventory ?? [],
    readyForTransport: data.readyForTransport ?? [],
  }
}

/** One HTTP request shared by all dashboard vehicle queries for the same date range. */
export const ensureDashboardVehiclesBundle = async (
  startMs: number,
  endMs: number,
): Promise<DashboardVehiclesApiBundle> => {
  const key = bundleKey(startMs, endMs)
  if (bundleCache?.key === key) {
    return bundleCache.promise
  }

  const promise = loadBundleFromApi(startMs, endMs)
    .catch(async apiError => {
      const [sold, active, warranty, purchasedForInventory, readyForTransport] = await Promise.all([
        fetchSoldVehiclesInRange(new Date(startMs), new Date(endMs)),
        fetchActiveInventoryVehicles(),
        fetchWarrantyVehicles(),
        fetchPurchasedForInventoryVehicles(),
        fetchVehiclesReadyForTransport(),
      ])
      if (!sold.length && !active.length && !warranty.length) {
        throw apiError
      }
      return {
        startMs,
        endMs,
        sold,
        active,
        warranty,
        purchasedForInventory,
        readyForTransport,
      }
    })
    .finally(() => {
      if (bundleCache?.key === key) {
        bundleCache = null
      }
    })

  bundleCache = { key, promise }
  return promise
}

export const fetchDashboardSoldInRange = async (start: Date, end: Date): Promise<Vehicle[]> => {
  const startMs = start.getTime()
  const endMs = end.getTime()
  const bundle = await ensureDashboardVehiclesBundle(startMs, endMs)
  return bundle.sold
}

const loadSliceFromBundle = async (
  pick: (bundle: DashboardVehiclesApiBundle) => Vehicle[],
): Promise<Vehicle[]> => {
  const { startMs, endMs } = getDashboardVehicleRangeMs()
  const bundle = await ensureDashboardVehiclesBundle(startMs, endMs)
  return pick(bundle)
}

export const fetchDashboardActiveInventory = async (): Promise<Vehicle[]> =>
  loadSliceFromBundle(bundle => bundle.active)

export const fetchDashboardWarrantyVehicles = async (): Promise<Vehicle[]> =>
  loadSliceFromBundle(bundle => bundle.warranty)

export const fetchDashboardPurchasedForInventory = async (): Promise<Vehicle[]> =>
  loadSliceFromBundle(bundle => bundle.purchasedForInventory)

export const fetchDashboardReadyForTransport = async (): Promise<Vehicle[]> =>
  loadSliceFromBundle(bundle => bundle.readyForTransport)
